import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Check } from 'lucide-react';

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  maxSize?: number;
  className?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  maxSize = 5 * 1024 * 1024, // 5MB
  className = ''
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setError('');
    
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setError(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setError('Only PDF files are allowed');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setUploadedFile(file);
      onFileSelect(file);
    }
  }, [maxSize, onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize,
    multiple: false
  });

  const removeFile = () => {
    setUploadedFile(null);
    onFileSelect(null);
    setError('');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer
          transition-colors duration-200 hover:border-blue-400 hover:bg-blue-50
          ${isDragActive ? 'border-blue-500 bg-blue-100' : ''}
          ${error ? 'border-red-500' : ''}
          bg-gray-50
        `}
      >
        <input {...getInputProps()} />
        
        {uploadedFile ? (
          <div className="flex items-center justify-center space-x-3">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-gray-700 text-sm font-medium">
              {uploadedFile.name}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeFile();
              }}
              className="text-red-500 hover:text-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <Upload className="w-8 h-8 text-gray-400" />
            <div className="text-gray-700">
              {isDragActive ? (
                <p className="text-sm font-medium">Drop your resume here...</p>
              ) : (
                <div className="text-sm">
                  <p className="font-medium">Drag & drop your resume here, or click to select</p>
                  <p className="text-xs text-gray-500 mt-1">PDF only, max {Math.round(maxSize / 1024 / 1024)}MB</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-red-600 text-sm font-medium">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;
